import { DataPage } from '@/app.routes';
import { FileHelper } from '@/helpers/file.helper';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AppConfigService } from '@/services/app-config.service';
import { AuthenticationService } from '@/services/authentication.service';
import { BridgeService } from '@/services/bridge/bridge.service';
import { NavService } from '@/services/nav.service';
import { NotificationCheckService } from '@/services/notification-check.service';

import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { PhotosService, RealProperty, UserSession } from '@eemo/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MainMenuListComponent } from '../main-menu-list/main-menu-list.component';

@Component({
  selector: 'app-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  imports: [MainMenuListComponent, TranslateModule],
})
export class MainMenuComponent implements OnInit, OnDestroy {
  private notificationCheckService = inject(NotificationCheckService);
  private userHelper = inject(UserHelper);
  private authenticationService = inject(AuthenticationService);
  private appConfigService = inject(AppConfigService);
  private navService = inject(NavService);
  private photoService = inject(PhotosService);
  private changeDetectorRefs = inject(ChangeDetectorRef);
  private fileHelper = inject(FileHelper);
  private propertyHelper = inject(PropertyHelper);

  emailValidated = true;
  public dropBoxCount: number;
  public sharingRequestCount: number;
  public pendingVisitFeedbackCount: number;

  private dropBoxCountSub = new Subscription();
  private sharingRequestCountSub = new Subscription();
  private pendingVisitFeedbackCountSub = new Subscription();
  private connectorsAvailableSub = new Subscription();
  private avatarSub = new Subscription();
  private navSub = new Subscription();
  private currentPageSub = new Subscription();
  private emailValidatedSub = new Subscription();
  private propChangedSub = new Subscription();

  public dropBoxMenuAvailable = false;
  public kpiMenuAvailable = false;
  public adminMenuAvailable = false;
  public connectorsMenuAvailable = false;
  public contactsMenuAvailable = false;
  public visitFeedbacksMenuAvailable = false;
  public saleMenuAvailable = false;
  public partnersMenuAvailable = false;
  public canUpdateProperty = false;

  currentProp: RealProperty | null;

  profileImg: SafeUrl;
  userDisplayName: string;
  user: UserSession;
  currentUid: string = null;
  menuInitialized = false;

  // top
  img: SafeUrl;
  title: string;
  subtitle: string;

  ngOnInit() {
    this.userDisplayName = this.userHelper.displayName;
    this.user = this.userHelper.currentUser;
    this.dropBoxMenuAvailable = false;
    this.kpiMenuAvailable = this.userHelper.isTeamEEMO() || this.userHelper.isRealEstate();
    // EEMO users access the referral administration only
    this.adminMenuAvailable = this.userHelper.isTeamEEMO();
    this.contactsMenuAvailable = this.userHelper.isRealEstate();
    this.visitFeedbacksMenuAvailable = this.userHelper.isRealEstate();

    this.canUpdateProperty = this.currentProp ? this.userHelper.canUpdateProperty(this.currentProp) : false;
    this.saleMenuAvailable = this.propertyHelper.isSaleMenuAvailable(this.currentProp);
    this.partnersMenuAvailable = environment.showPublicPartners;

    this.appConfigService.selectedPropObs.subscribe((p) => {
      this.canUpdateProperty = p ? this.userHelper.canUpdateProperty(p) : false;
      this.saleMenuAvailable = this.propertyHelper.isSaleMenuAvailable(p);
      if (this.menuInitialized) {
        this.setMenu();
      }
    });

    this.connectorsAvailableSub = this.appConfigService.availableConnectorsObs.subscribe((connectors) => {
      this.connectorsMenuAvailable = connectors && connectors.length > 0;
      if (this.menuInitialized) {
        this.setMenu();
      }
    });

    this.dropBoxCountSub = this.notificationCheckService.dropBoxCount.subscribe((count) => {
      if (count != null) {
        this.dropBoxCount = count.global;
        this.dropBoxMenuAvailable = this.dropBoxCount > 0;
        if (this.menuInitialized) {
          this.setMenu();
        }
      }
    });

    this.sharingRequestCountSub = this.notificationCheckService.sharingRequestCount.subscribe((count) => {
      if (count !== -1) {
        this.sharingRequestCount = count;
        if (this.menuInitialized) {
          this.setMenu();
        }
      }
    });

    this.pendingVisitFeedbackCountSub = this.notificationCheckService.pendingVisitFeedbackCount.subscribe((count) => {
      if (count !== -1) {
        this.pendingVisitFeedbackCount = count;
        if (this.menuInitialized) {
          this.setMenu();
        }
      }
    });

    this.emailValidatedSub = this.authenticationService.emailValidated.subscribe((validation) => {
      this.emailValidated = validation;
      if (this.menuInitialized) {
        this.setMenu();
      }
    });

    this.avatarSub = this.authenticationService.currentAvatar.subscribe((file: Blob) => {
      this.profileImg = this.fileHelper.imageApiToHtml(file);
    });

    this.navSub = this.navService.currentDataPage$.subscribe((dataPage: DataPage | null) => {
      this.onNewUid(dataPage?.uid || null);
    });

    // wait for the dropbox count, email validated and current page before setting the menu
    Promise.all([
      this.authenticationService.emailValidated,
      this.notificationCheckService.dropBoxCount,
      this.notificationCheckService.sharingRequestCount,
      this.navService.currentDataPage$,
    ]).then(() => {
      this.setMenu();
      this.menuInitialized = true;
    });

    this.currentPageSub = this.navService.currentDataPage$.subscribe((dataPage) => {
      if (dataPage?.uid) {
        this.currentProp = this.appConfigService.selectedProp;
        this.title = this.currentProp?.name || '';
        this.subtitle = this.currentProp?.type ? 'enum.PropertyType.' + this.currentProp.type : '';
        this.changeDetectorRefs.detectChanges();
        if (this.currentProp) {
          this.loadPropertyMainPhoto(this.currentProp);
        }
      } else {
        this.currentProp = null;
        this.changeDetectorRefs.detectChanges();
      }
    });
  }

  ngOnDestroy() {
    this.dropBoxCountSub.unsubscribe();
    this.sharingRequestCountSub.unsubscribe();
    this.pendingVisitFeedbackCountSub.unsubscribe();
    this.connectorsAvailableSub.unsubscribe();
    this.avatarSub.unsubscribe();
    this.navSub.unsubscribe();
    this.currentPageSub.unsubscribe();
    this.emailValidatedSub.unsubscribe();
    this.propChangedSub.unsubscribe();
  }

  logout() {
    this.authenticationService.logout();
  }

  setMenu() {
    const currentUid: string | null = this.navService.currentDataPage$.getValue()?.uid || null;
    this.navService.menuHandler(
      this.dropBoxMenuAvailable,
      this.dropBoxCount,
      this.sharingRequestCount,
      this.connectorsMenuAvailable,
      this.contactsMenuAvailable,
      this.visitFeedbacksMenuAvailable,
      this.pendingVisitFeedbackCount,
      this.kpiMenuAvailable,
      this.adminMenuAvailable,
      this.saleMenuAvailable,
      this.partnersMenuAvailable,
      this.canUpdateProperty,
      this.emailValidated,
      this.currentProp ? this.currentProp.allowRescue : false,
      currentUid,
    );
    BridgeService.newMenu(
      this.navService.genMenuItems(
        this.dropBoxMenuAvailable,
        this.dropBoxCount,
        this.sharingRequestCount,
        this.connectorsMenuAvailable,
        this.contactsMenuAvailable,
        this.visitFeedbacksMenuAvailable,
        this.pendingVisitFeedbackCount,
        this.kpiMenuAvailable,
        this.adminMenuAvailable,
        this.saleMenuAvailable,
        this.partnersMenuAvailable,
        this.canUpdateProperty,
        this.emailValidated,
        this.currentProp ? this.currentProp.allowRescue : false,
        currentUid,
      ),
    );
  }

  onNewUid(uid: string | null) {
    this.currentUid = uid;
    if (this.menuInitialized) {
      this.setMenu();
    }
  }

  private loadPropertyMainPhoto(prop: RealProperty) {
    this.photoService.getMainPhotoImage(prop.uid, '271x169').subscribe((photo) => {
      this.img = this.fileHelper.imageApiToHtml(photo);
    });
  }
}
